import React, { useState, useEffect, useRef } from 'react';
import { askCopilot } from './pages_build';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Legend, ComposedChart
} from 'recharts';
import { AURAVEX_LOGO } from './logo';
import { Badge, Dot, KPI, Panel, Prog, Pill, BtnStyles, Btn, Inp, Sel, Textarea, Modal, Toast, DataTable, PageHeader, AlertBox, Grid, Divider, CameraLiveModal } from './utils';
import { DEFAULT_PAGE_ACCESS, NAV_SECTIONS, ALL_PAGES } from './pages_stream';
import { PageInfra, PageAudit, PageNotifications, PageAIAgent, AI_RESPONSES, CHAT_INIT, SPEECH_LANGS } from './pages_admin';

// ============================================================
// LIVE API INTEGRATION - AURAVEX Backend
// ============================================================
const API_NODE   = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL : '';
const API_PYTHON = (typeof process !== 'undefined' && process.env?.REACT_APP_PYTHON_URL)
  ? process.env.REACT_APP_PYTHON_URL : 'http://localhost:8000';
const WS_URL_BASE = (typeof process !== 'undefined' && process.env?.REACT_APP_WS_URL)
  ? process.env.REACT_APP_WS_URL : 'ws://localhost:5000/ws';

const apiFetch = async (path, opts = {}, base = API_NODE) => {
  try {
    const token = localStorage.getItem('auravex_token');
    const res = await fetch(base + path, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...opts.headers,
      },
      ...opts,
    });
    if (res.status === 401) {
      localStorage.removeItem('auravex_token');
      localStorage.removeItem('auravex_user');
      window.dispatchEvent(new CustomEvent('auravex:401'));
      return null;
    }
    if (!res.ok) {
      let errMsg = `HTTP ${res.status}`;
      try { const j = await res.json(); errMsg = j.error || j.message || errMsg; } catch {}
      throw new Error(errMsg);
    }
    return await res.json();
  } catch (e) {
    console.warn(`[API] ${opts.method||'GET'} ${path}:`, e.message);
    throw e;  // re-throw so callers can handle it
  }
};
const pyFetch   = (p, o={}) => apiFetch(p, o, API_PYTHON);
const apiPost   = (p, d) => apiFetch(p, { method: 'POST',   body: JSON.stringify(d) });
const apiPut    = (p, d) => apiFetch(p, { method: 'PUT',    body: JSON.stringify(d) });
const apiPatch  = (p, d) => apiFetch(p, { method: 'PATCH',  body: JSON.stringify(d) });
const apiDelete = (p)    => apiFetch(p, { method: 'DELETE' });

// Map DB snake_case -> frontend camelCase
const mapTenant = r => r ? ({
  ...r,
  plan:    r.plan_id   ?? r.plan   ?? 'starter',
  maxCam:  r.max_cameras ?? r.max_cam ?? r.maxCam ?? 0,
  maxU:    r.max_users   ?? r.max_u   ?? r.maxU   ?? 0,
}) : null;
const mapCamera = r => r ? ({
  ...r,
  siteId:  r.site_id    ?? r.siteId  ?? '',
  res:     r.resolution ?? r.res     ?? '1080p',
  models:  Array.isArray(r.models) ? r.models : (typeof r.models==='string' ? JSON.parse(r.models||'[]') : []),
}) : null;
const mapEvent  = r => r ? ({
  ...r,
  caseId:  r.case_id   ?? r.caseId  ?? '',
  tId:     r.tenant_id ?? r.t_id    ?? r.tId    ?? '',
  siteId:  r.site_id   ?? r.siteId  ?? '',
  camId:   r.cam_id    ?? r.camId   ?? '',
  sev:     r.severity  ?? r.sev     ?? 'Medium',
  evidence: Array.isArray(r.evidence) ? r.evidence : [],
}) : null;
const mapUser   = r => r ? ({
  id:        r.id,
  name:      r.name,
  email:     r.email,
  role:      r.role,
  tenantId:  r.tenant_id   ?? r.tenantId ?? '',
  status:    r.status      ?? 'Active',
  mfa:       r.mfa_enabled ?? r.mfa      ?? false,
  last:      r.last_login  ?? r.last_seen ?? r.last ?? '',
  mobiles:   Array.isArray(r.mobiles)  ? r.mobiles  : (typeof r.mobiles ==='string' ? JSON.parse(r.mobiles  ||'[]') : []),
  emails:    Array.isArray(r.emails)   ? r.emails   : (typeof r.emails  ==='string' ? JSON.parse(r.emails   ||'[]') : [r.email||'']),
  notifyVia: (typeof r.notify_via==='object' && r.notify_via!==null) ? r.notify_via : (typeof r.notify_via==='string' ? JSON.parse(r.notify_via||'{}') : r.notifyVia || {email:true}),
}) : null;
const mapJetson = r => r ? ({ ...r, siteId: r.site_id??r.siteId, tId: r.tenant_id??r.t_id??r.tId }) : null;
const mapViol   = r => r ? ({ ...r, camId: r.cam_id??r.camId }) : null;
/* -- API payload converters (Frontend → Backend field names) -- */
const tenantToAPI  = t => ({ name:t.name, sector:t.sector, plan_id:t.plan||t.plan_id||'starter', contact:t.contact||'', billing:t.billing||'monthly', renewal:t.renewal||null, status:t.status, onboarded:t.onboarded||false });
const cameraToAPI  = c => ({ site_id:c.siteId||c.site_id, name:c.name, brand:c.brand||'Hikvision', type:c.type||'Fixed', ip:c.ip||'', resolution:c.res||c.resolution||'1080p', fps:c.fps||25, models:c.models||[], retention:c.retention||'30d', zone:c.zone||'', status:c.status, health:c.health });
const userToAPI    = u => ({ name:u.name, email:u.email||(u.emails&&u.emails[0])||'', role:u.role, tenant_id:u.tenantId||u.tenant_id||null, mobiles:u.mobiles||[], emails_list:u.emails||[], notify_via:u.notifyVia||u.notify_via||{email:true}, mfa_enabled:u.mfa||false, status:u.status });
const ruleToAPI    = r => ({ name:r.name, trigger:r.trigger||'', condition:r.condition||'', action:r.action||'', tenant_id:r.tId||r.tenant_id||'All', status:r.status||'Active' });

/* -- SECTOR AVATARS ------------------------------------------- */
const AVATAR_GOV  = "data:image/jpeg;base64,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";   // Male  - Government
const AVATAR_PVT  = "data:image/jpeg;base64,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"; // Female - Private / Industrial
const sectorAvatar = (sector) =>
  (sector==="Government"||sector==="Municipal"||sector==="Defence"||sector==="Police")
    ? AVATAR_GOV : AVATAR_PVT;

/* -- DESIGN TOKENS -- CLEAN WHITE LIGHT THEME ------------------- */
const T = {
  /* -- Surface hierarchy -- */
  bg:"#F0F2F7",     /* Page background -- very light grey-blue */
  deep:"#FFFFFF",   /* Sidebar background */
  panel:"#FFFFFF",  /* Card panels */
  card:"#FFFFFF",   /* Inline cards */
  border:"#E2E8F3", /* Subtle borders */
  hi:"#EEF2FB",     /* Hover highlight */
  glow:"#DDE5F6",   /* Active glow */
  faint:"#F7F9FD",  /* Input backgrounds */
  /* Gold primary -- AURAVEX brand */
  accent:"#C48A0E", accentD:"#9A6B00", accentLo:"#C48A0E18",
  /* Electric blue */
  blue:"#1A5FB4",   blueLo:"#1A5FB414",
  /* Green */
  green:"#1E8A55",  greenLo:"#1E8A5514",
  /* Standard semantic */
  red:"#C0392B",    redLo:"#C0392B12",
  purple:"#6527BE", purpleLo:"#6527BE12",
  teal:"#0E7490",   tealLo:"#0E749012",
  orange:"#C05800", orangeLo:"#C0580012",
  pink:"#A21060",   pinkLo:"#A2106012",
  /* Text -- dark on white */
  text:"#1A2235",   muted:"#7A90B0",
  /* Silver */
  silver:"#4A607A",
  font:"'DM Sans',system-ui,sans-serif",
  mono:"'IBM Plex Mono','Fira Mono',monospace",
};

export const Sidebar = ({active,setActive,events,tenants,cameras,currentRole,pageAccess}) => {
  const openE      = events.filter(e=>e.status==="Open").length;
  const trialT     = tenants.filter(t=>t.status==="Trial").length;
  const offlineC   = cameras.filter(c=>c.status==="Offline").length;

  const getBadge = (key) => {
    if(key==="events")    return openE>0 ? openE : null;
    if(key==="tenants")   return trialT>0 ? trialT : null;
    if(key==="cameras")   return offlineC>0 ? offlineC : null;
    if(key==="ai")        return "NEW";
    if(key==="live")      return "●";
    if(key==="incidents") return events.filter(e=>e.status==="Open").length||null;
    return null;
  };

  // Determine allowed pages for current role
  const isSuperAdmin = currentRole === "superadmin";
  const allowed = isSuperAdmin
    ? null  // null = all pages
    : new Set(pageAccess[currentRole] || []);

  // Role display config
  const roleColors = {superadmin:T.accent, government:"#3B82F6", private:"#7C3AED"};
  const roleLabels = {superadmin:"Super Admin", government:"Tenant Admin", private:"User"};
  const roleIcons  = {superadmin:"SA", government:"🏛", private:"🏢"};
  const roleColor  = roleColors[currentRole] || T.accent;

  return(
    <div style={{
      width:220,flexShrink:0,background:T.deep,
      borderRight:`1px solid ${T.border}`,
      display:"flex",flexDirection:"column",
      height:"100vh",overflowY:"auto",
    }}>
      {/* Logo */}
      <div style={{padding:"14px 16px 12px",borderBottom:`1px solid ${T.border}`,background:"linear-gradient(180deg,#FFFFFF,#F8FAFD)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <img src={AURAVEX_LOGO} alt="AURAVEX" style={{width:40,height:40,borderRadius:8,objectFit:"cover",flexShrink:0,border:`1px solid ${T.accent}55`,boxShadow:`0 0 10px ${T.accent}22`}}/>
          <div>
            <div style={{fontSize:13,fontWeight:800,background:`linear-gradient(90deg,${T.accent},#E8C060)`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",letterSpacing:.8,lineHeight:1.1}}>AURAVEX</div>
            <div style={{fontSize:9,color:T.blue,fontWeight:700,letterSpacing:1.5,lineHeight:1.3}}>GLOBAL AI</div>
            <div style={{fontSize:7,color:T.muted,letterSpacing:.3,fontFamily:T.mono,marginTop:1}}>Engineering Intelligent Authority</div>
          </div>
        </div>
        {/* Role indicator strip */}
        {!isSuperAdmin&&(
          <div style={{marginTop:8,background:`${roleColor}18`,border:`1px solid ${roleColor}44`,borderRadius:6,padding:"4px 8px",display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:11}}>{roleIcons[currentRole]}</span>
            <span style={{fontSize:9,fontWeight:700,color:roleColor}}>{roleLabels[currentRole]} View</span>
            <span style={{marginLeft:"auto",fontSize:8,color:roleColor,fontFamily:T.mono}}>{allowed?.size||0} pages</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <div style={{flex:1,padding:"8px 8px",overflowY:"auto"}}>
        {NAV_SECTIONS.map(section=>{
          // Filter items based on role
          const visibleItems = section.items.filter(item=>{
            if(item.roles && !item.roles.includes(currentRole)) return false;  // role-specific item (e.g. "Request a Model" for users)
            if(isSuperAdmin) return true;  // SA sees everything
            if(item.id==="accesscontrol") return false;  // only SA can access this
            return allowed.has(item.id);
          });
          if(visibleItems.length===0) return null;  // hide empty sections
          return(
            <div key={section.label} style={{marginBottom:4}}>
              <div style={{fontSize:8,color:T.muted,fontWeight:800,letterSpacing:1.5,padding:"10px 10px 4px",textTransform:"uppercase"}}>{section.label}</div>
              {visibleItems.map(item=>{
                const isActive = active===item.id;
                const badgeVal = item.badge ? getBadge(item.badge) : null;
                return(
                  <button key={item.id} onClick={()=>setActive(item.id)} style={{
                    width:"100%",display:"flex",alignItems:"center",gap:8,
                    padding:"8px 10px",borderRadius:8,border:"none",
                    background:isActive?`linear-gradient(90deg,${T.accent}1A,${T.accent}06)`:"transparent",
                    cursor:"pointer",fontFamily:T.font,transition:"background .12s",
                    borderLeft:isActive?`2px solid ${T.accent}`:"2px solid transparent",
                  }}>
                    <span style={{fontSize:14,flexShrink:0}}>{item.icon}</span>
                    <span style={{fontSize:11,fontWeight:isActive?700:500,color:isActive?T.accent:T.muted,flex:1,textAlign:"left"}}>{item.label}</span>
                    {badgeVal!==null&&(
                      <span style={{
                        background:typeof badgeVal==="string"?T.purple:T.red,
                        color:"#fff",fontSize:8,fontWeight:800,padding:"1px 5px",
                        borderRadius:10,fontFamily:T.mono,minWidth:16,textAlign:"center",
                      }}>{badgeVal}</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{padding:"12px 14px",borderTop:`1px solid ${T.border}`,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
          <div style={{width:28,height:28,borderRadius:"50%",background:roleColor,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0,color:"#000",fontWeight:800}}>{typeof roleIcons[currentRole]==="string"&&roleIcons[currentRole].length<=2?roleIcons[currentRole]:"🏢"}</div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:T.text}}>{roleLabels[currentRole]}</div>
            <div style={{fontSize:9,color:T.muted}}>{(()=>{try{return JSON.parse(localStorage.getItem("auravex_user")||"{}").email||""}catch{return ""}})()}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:6}}>
          <Dot s="Online"/>
          <span style={{fontSize:9,color:T.muted,fontFamily:T.mono,marginLeft:"auto"}}>v4.2.1</span>
        </div>
      </div>
    </div>
  );
};

/* -- TOP BAR ------------------------------------------------------ */
// The logged-in user's real role (stored at login), independent of the preview switcher.
const isPlatformAdmin = () => { try { const r = JSON.parse(localStorage.getItem('auravex_user')||'{}').role; return r==="superadmin"||r==="Super Admin"; } catch { return false; } };

export const TopBar = ({active,events,time,currentRole,setCurrentRole,loginUser,onLogout}) => {
  const openE  = events.filter(e=>e.status==="Open"&&e.sev==="Critical").length;
  const [showRoles,setShowRoles] = useState(false);
  const [showUserMenu,setShowUserMenu] = useState(false);
  const PAGE_TITLES = {
    dashboard:"Dashboard",livestream:"📺 Live Camera Streaming",incidentlog:"🗂 Incident Recall Log",
    tenants:"Tenant Management",subscriptions:"Subscriptions & Billing",
    users:"Users & Access Control",cameras:"Sites - Zones - Cameras",infrastructure:"AI Edge Server Infrastructure",
    models:"AI Models",licences:"Model Licences",training:"Build a Model",modelrequests:"Model Requests",mqttagents:"Edge Agents",rules:"Rule Engine",events:"Events & Evidence",
    traffic:"Smart Traffic Vertical",notifications:"Notification Center",reports:"Reporting Engine",
    aiagent:"AI Copilot",audit:"Audit Trail",accesscontrol:"🔐 Page Access Control",
  };
  const roleColors = {superadmin:T.accent, government:"#3B82F6", private:"#7C3AED"};
  const roleLabels = {superadmin:"Super Admin", government:"Tenant Admin", private:"User"};
  const rc = roleColors[currentRole]||T.accent;
  return(
    <div style={{
      height:50,flexShrink:0,background:T.panel,
      borderBottom:`1px solid ${T.border}`,
      display:"flex",alignItems:"center",padding:"0 20px",
      justifyContent:"space-between",position:"relative",
    }}>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <img src={AURAVEX_LOGO} alt="AURAVEX" style={{width:26,height:26,borderRadius:5,objectFit:"cover",border:`1px solid ${T.accent}44`}}/>
        <div style={{width:1,height:20,background:T.border}}/>
        <span style={{fontSize:13,fontWeight:700,color:T.text}}>{PAGE_TITLES[active]||"AURAVEX GLOBAL AI"}</span>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        {openE>0&&(
          <div style={{display:"flex",alignItems:"center",gap:6,background:T.redLo,border:`1px solid ${T.red}44`,borderRadius:7,padding:"4px 10px"}}>
            <span style={{width:6,height:6,borderRadius:"50%",background:T.red,animation:"blink 1s infinite"}}/>
            <span style={{fontSize:10,fontWeight:700,color:T.red}}>{openE} Critical Open</span>
          </div>
        )}
        <span style={{fontSize:11,color:T.muted,fontFamily:T.mono}}>{time}</span>
        <div style={{display:"flex",gap:6}}>
          {/* removed debug */}
          {/* removed debug */}
        </div>

        {/* Role Switcher */}
        <div style={{position:"relative"}}>
          <button
            onClick={()=>{ if(isPlatformAdmin()) setShowRoles(p=>!p); }}
            style={{display:"flex",alignItems:"center",gap:6,background:`${rc}15`,border:`1px solid ${rc}55`,borderRadius:8,padding:"5px 10px",cursor:isPlatformAdmin()?"pointer":"default",fontFamily:T.font}}>
            <div style={{width:20,height:20,borderRadius:"50%",background:rc,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#000",fontWeight:800,flexShrink:0}}>
              {currentRole==="superadmin"?"SA":currentRole==="government"?"🏛":"🏢"}
            </div>
            <span style={{fontSize:10,fontWeight:700,color:rc}}>{roleLabels[currentRole]}</span>
            {isPlatformAdmin()&&<span style={{fontSize:9,color:rc,opacity:.7}}>▼</span>}
          </button>
          {showRoles&&isPlatformAdmin()&&(
            <div style={{
              position:"absolute",top:"calc(100% + 6px)",right:0,
              background:T.card,border:`1px solid ${T.border}`,borderRadius:10,
              padding:8,zIndex:999,minWidth:220,
              boxShadow:"0 8px 32px rgba(26,34,53,.18)",
            }}>
              <div style={{fontSize:9,color:T.muted,fontWeight:800,letterSpacing:1,padding:"4px 8px 8px",textTransform:"uppercase"}}>Preview as User Role</div>
              {[
                {id:"superadmin", icon:"SA",   label:"Super Admin",     sub:"All pages - Full access",         c:T.accent},
                {id:"government", icon:"🏛",   label:"Tenant Admin",    sub:"Preview the tenant admin view",    c:"#3B82F6"},
                {id:"private",    icon:"🏢",   label:"User",            sub:"Preview the basic user view",      c:"#7C3AED"},
              ].map(r=>(
                <button key={r.id} onClick={()=>{setCurrentRole(r.id);setShowRoles(false);}}
                  style={{
                    width:"100%",display:"flex",alignItems:"center",gap:10,padding:"8px 10px",
                    borderRadius:8,border:currentRole===r.id?`1px solid ${r.c}55`:"1px solid transparent",
                    background:currentRole===r.id?`${r.c}15`:"transparent",cursor:"pointer",
                    fontFamily:T.font,marginBottom:2,
                  }}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:`${r.c}22`,border:`1px solid ${r.c}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0,fontWeight:800,color:r.c}}>{r.icon}</div>
                  <div style={{textAlign:"left",flex:1}}>
                    <div style={{fontSize:11,fontWeight:700,color:currentRole===r.id?r.c:T.text}}>{r.label}</div>
                    <div style={{fontSize:9,color:T.muted}}>{r.sub}</div>
                  </div>
                  {currentRole===r.id&&<span style={{fontSize:11,color:r.c}}>✓</span>}
                </button>
              ))}
              <div style={{borderTop:`1px solid ${T.border}`,marginTop:4,paddingTop:6,paddingLeft:10}}>
                <div style={{fontSize:8,color:T.muted}}>Switching role previews page visibility. Access Control → System menu.</div>
              </div>
            </div>
          )}
        </div>

        {/* User Menu + Logout */}
        <div style={{position:"relative"}}>
          <button onClick={()=>setShowUserMenu(p=>!p)}
            style={{display:"flex",alignItems:"center",gap:7,background:T.faint,
              border:`1px solid ${T.border}`,borderRadius:8,padding:"5px 10px",cursor:"pointer",fontFamily:T.font}}>
            <div style={{width:22,height:22,borderRadius:"50%",background:rc,display:"flex",alignItems:"center",
              justifyContent:"center",fontSize:10,color:"#000",fontWeight:800}}>
              {loginUser?.name?.[0]||"A"}
            </div>
            <div style={{textAlign:"left"}}>
              <div style={{fontSize:10,fontWeight:700,color:T.text,lineHeight:1.2}}>{loginUser?.name||"Admin"}</div>
              <div style={{fontSize:9,color:T.muted}}>Logged in</div>
            </div>
            <span style={{fontSize:9,color:T.muted}}>▼</span>
          </button>
          {showUserMenu&&(
            <div style={{position:"absolute",top:"calc(100% + 6px)",right:0,background:T.card,
              border:`1px solid ${T.border}`,borderRadius:10,padding:8,zIndex:999,minWidth:180,
              boxShadow:"0 8px 32px rgba(26,34,53,.18)"}}>
              <div style={{padding:"8px 10px 4px"}}>
                <div style={{fontSize:11,fontWeight:700,color:T.text}}>{loginUser?.name}</div>
                <div style={{fontSize:9,color:T.muted,marginTop:2}}>
                  {loginUser?.role==="superadmin"?"Super Admin":loginUser?.role==="government"?"Tenant Admin":"User"}
                </div>
              </div>
              <div style={{borderTop:`1px solid ${T.border}`,margin:"6px 0"}}/>
              <button onClick={()=>{setShowUserMenu(false);onLogout&&onLogout();}}
                style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:"8px 10px",
                  borderRadius:7,border:"none",background:"transparent",cursor:"pointer",
                  fontFamily:T.font,color:T.red,fontSize:12,fontWeight:600}}>
                🚪 Logout
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

/* -- FLOATING AI AVATAR -------------------------------------------- */
export const FloatingAIAvatar = ({currentRole}) => {
  const [open,     setOpen]    = useState(false);
  const [chat,     setChat]    = useState(CHAT_INIT);
  const [input,    setInput]   = useState("");
  const [loading,  setLoading] = useState(false);
  const [pulse,    setPulse]   = useState(true);
  /* -- Speech state -- */
  const [listening,    setListening]    = useState(false);
  const [micPulse,     setMicPulse]     = useState(false);
  const [langCode,     setLangCode]     = useState("en-IN");
  const [ttsEnabled,   setTtsEnabled]   = useState(true);
  const [transcript,   setTranscript]   = useState("");
  const [speechSupport,setSpeechSupport]= useState(false);
  const [ttsSupport,   setTtsSupport]   = useState(false);
  const [speechErr,    setSpeechErr]    = useState("");
  const [showLang,     setShowLang]     = useState(false);
  const chatEnd  = useRef(null);
  const recogRef = useRef(null);

  useEffect(()=>{ chatEnd.current?.scrollIntoView({behavior:"smooth"}); },[chat]);
  useEffect(()=>{ const t=setTimeout(()=>setPulse(false),4000); return()=>clearTimeout(t); },[]);
  useEffect(()=>{
    setSpeechSupport(!!(window.SpeechRecognition||window.webkitSpeechRecognition));
    setTtsSupport(!!window.speechSynthesis);
    if(navigator?.permissions){
      navigator.permissions.query({name:"microphone"}).then(res=>{
        if(res.state==="denied") setSpeechErr("🔒 Mic blocked -- allow in browser settings");
        res.onchange = ()=>{
          if(res.state==="granted") setSpeechErr("");
          if(res.state==="denied") setSpeechErr("🔒 Mic blocked -- allow in browser settings");
        };
      }).catch(()=>{});
    }
  },[]);

  const roleColors = {superadmin:T.accent, government:"#3B82F6", private:"#7C3AED"};
  const rc = roleColors[currentRole]||T.accent;

  const QUICK = [
    {label:"📹 Camera status",      key:"Which of my cameras are offline right now?"},
    {label:"🚨 Open events",        key:"Summarise my open events and the most frequent event types this week"},
    {label:"🧠 Models & licences",  key:"Which models do I have and when do their licences expire?"},
    {label:"⚡ Edge servers",       key:"Are my AI Edge Servers online?"},
  ];

  /* -- STT -- */
  const requestMicAndListen = async () => {
    setSpeechErr(""); setTranscript("");
    // Step 1: Request mic permission via getUserMedia first (triggers browser dialog)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio:true});
      // Permission granted -- stop the stream immediately, we only needed the dialog
      stream.getTracks().forEach(t=>t.stop());
    } catch(err) {
      if(err.name==="NotAllowedError"||err.name==="PermissionDeniedError") {
        setSpeechErr("🔒 Mic blocked. Click the 🔒 icon in your browser address bar -> Allow microphone.");
      } else if(err.name==="NotFoundError") {
        setSpeechErr("🎤 No microphone found. Please connect a mic and try again.");
      } else {
        setSpeechErr("Mic unavailable: "+err.message);
      }
      return;
    }
    // Step 2: Now start SpeechRecognition
    const SR = window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR){ setSpeechErr("Speech recognition not supported. Please use Chrome or Edge."); return; }
    const r = new SR();
    r.lang = langCode; r.interimResults = true; r.maxAlternatives = 1; r.continuous = false;
    r.onstart  = ()=>{ setListening(true); setMicPulse(true); };
    r.onresult = (e)=>{ const t=Array.from(e.results).map(x=>x[0].transcript).join(" "); setTranscript(t); setInput(t); };
    r.onend    = ()=>{ setListening(false); setMicPulse(false); };
    r.onerror  = (e)=>{
      setListening(false); setMicPulse(false);
      const msgs = {
        "not-allowed":  "🔒 Mic blocked. Allow microphone in browser settings.",
        "no-speech":    "🔇 No speech detected. Please speak clearly.",
        "network":      "🌐 Network error with speech service.",
        "aborted":      "Recording stopped.",
        "audio-capture":"🎤 Microphone not accessible.",
        "service-not-allowed": "🔒 Speech service blocked. Use HTTPS or allow in settings.",
      };
      setSpeechErr(msgs[e.error]||`Speech error: ${e.error}`);
    };
    recogRef.current = r;
    try { r.start(); } catch(e){ setSpeechErr("Could not start recording: "+e.message); }
  };
  const startListening = requestMicAndListen;
  const stopListening = ()=>{ recogRef.current?.stop(); setListening(false); setMicPulse(false); };

  /* -- TTS core -- */
  const [speakLoading, setSpeakLoading] = useState(false);

  const doSpeak = (text, lang) => {
    if(!ttsSupport) return;
    const clean = text.replace(/<[^>]+>/g,"").trim();
    if(!clean) return;
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = lang || langCode; u.rate = 0.95; u.pitch = 1.05;
    const trySpeak = () => {
      const voices = window.speechSynthesis.getVoices();
      const match = voices.find(v=>v.lang===u.lang)||voices.find(v=>v.lang.startsWith(u.lang.split("-")[0]));
      if(match) u.voice = match;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    };
    if(window.speechSynthesis.getVoices().length > 0) trySpeak();
    else { window.speechSynthesis.onvoiceschanged = trySpeak; }
  };

  const speakText = async (text) => {
    if(!ttsEnabled || !ttsSupport) return;
    const clean = text.replace(/<[^>]+>/g,"").replace(/&[a-z]+;/gi,"").trim();
    if(!clean) return;
    // English -- speak directly
    if(langCode === "en-IN" || langCode === "en-US" || langCode === "en-GB") {
      doSpeak(clean, langCode); return;
    }
    // Non-English: speak the English text with the selected voice (no third-party translation from the browser).
    doSpeak(clean, langCode);
  };
  const stopSpeaking = ()=>window.speechSynthesis?.cancel();

  const sendMsg = (text) => {
    const q = (text||input).trim();
    if(!q) return;
    setTranscript(""); setSpeechErr("");
    setChat(p=>[...p,{role:"user",msg:q,time:new Date().toLocaleTimeString()}]);
    setInput(""); setLoading(true);
    const history = chat.filter(m=>m.role==="user"||m.role==="ai").slice(-8).map(m=>({role:m.role==="ai"?"assistant":"user",content:String(m.msg).replace(/<[^>]+>/g,"")}));
    askCopilot(q, history).then(r=>{
      const reply = String(r.reply||"").replace(/\n/g,"<br>");
      setChat(p=>[...p,{role:"ai",msg:reply,time:new Date().toLocaleTimeString()}]);
      speakText(reply);
    }).catch(e=>{
      setChat(p=>[...p,{role:"ai",msg:`Sorry — ${e.message}`,time:new Date().toLocaleTimeString()}]);
    }).finally(()=>setLoading(false));
  };

  /* Top 6 languages for quick pick */
  const TOP_LANGS = SPEECH_LANGS.slice(0,6);

  return(
    <div style={{position:"fixed",bottom:24,right:24,zIndex:9999,display:"flex",flexDirection:"column",alignItems:"flex-end",gap:10,pointerEvents:"none"}}>

      {/* -- Chat panel -- */}
      {open&&(
        <div style={{
          width:380,height:560,background:T.card,
          border:`1px solid ${rc}44`,borderRadius:16,
          display:"flex",flexDirection:"column",overflow:"hidden",
          boxShadow:`0 8px 32px rgba(26,34,53,.18), 0 0 0 1px ${rc}22`,
          pointerEvents:"all",animation:"slideIn .2s ease",
        }}>
          {/* Header */}
          <div style={{padding:"12px 14px",background:`linear-gradient(135deg,${T.faint},${T.hi})`,borderBottom:`1px solid ${rc}33`,display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <div style={{width:36,height:36,borderRadius:"50%",overflow:"hidden",flexShrink:0,border:`2px solid ${T.teal}55`}}>
              <img src={AVATAR_PVT} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}} alt="AI"/>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:11,fontWeight:800,color:T.text}}>AURAVEX AI Copilot</div>
              <div style={{fontSize:9,color:listening?T.red:T.green,display:"flex",alignItems:"center",gap:4}}>
                {listening
                  ? <>{[1,2,3,4].map(n=><span key={n} style={{display:"inline-block",width:2,background:T.red,borderRadius:1,height:3+n*2,animation:`waveBar ${0.5+n*.1}s ease-in-out infinite`,animationDelay:`${n*.1}s`}}/>)}<span style={{marginLeft:3}}>Recording...</span></>
                  : <><span style={{width:5,height:5,borderRadius:"50%",background:T.green,display:"inline-block"}}/><span>Online - Real-time analysis</span></>
                }
              </div>
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <span style={{fontSize:9,color:rc,fontWeight:700,background:`${rc}18`,border:`1px solid ${rc}33`,padding:"2px 6px",borderRadius:6}}>
                {currentRole==="superadmin"?"👑 SA":currentRole==="government"?"🏛 Gov":"🏢 Pvt"}
              </span>
              {ttsSupport&&ttsEnabled&&(
                <button onClick={stopSpeaking} title="Stop speaking"
                  style={{background:"none",border:`1px solid ${T.border}`,borderRadius:6,padding:"2px 6px",cursor:"pointer",fontSize:9,color:T.muted,fontFamily:T.font}}>⏹</button>
              )}
              <button onClick={()=>setOpen(false)} style={{background:"none",border:"none",color:T.muted,fontSize:14,cursor:"pointer",lineHeight:1,padding:2}}>✕</button>
            </div>
          </div>

          {/* Quick prompts */}
          <div style={{padding:"6px 10px",borderBottom:`1px solid ${T.border}`,display:"flex",gap:5,flexWrap:"wrap",flexShrink:0,background:T.deep}}>
            {QUICK.map(q=>(
              <button key={q.key} onClick={()=>sendMsg(q.label.split(" ").slice(1).join(" "))}
                style={{fontSize:9,padding:"3px 8px",borderRadius:12,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontFamily:T.font,whiteSpace:"nowrap",transition:"all .12s"}}
                onMouseEnter={e=>{e.target.style.background=`${rc}18`;e.target.style.borderColor=rc;e.target.style.color=rc;}}
                onMouseLeave={e=>{e.target.style.background="transparent";e.target.style.borderColor=T.border;e.target.style.color=T.muted;}}>
                {q.label}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div style={{flex:1,overflowY:"auto",padding:10,display:"flex",flexDirection:"column",gap:8}}>
            {chat.map((m,i)=>(
              <div key={i} style={{display:"flex",gap:7,alignItems:"flex-start",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
                {m.role==="ai"&&(
                  <div style={{width:26,height:26,borderRadius:"50%",overflow:"hidden",flexShrink:0,marginTop:2,border:`1.5px solid ${T.teal}44`}}>
                    <img src={AVATAR_PVT} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}} alt="AI"/>
                  </div>
                )}
                <div style={{display:"flex",flexDirection:"column",gap:2,maxWidth:"80%"}}>
                  <div style={{
                    padding:"8px 10px",
                    borderRadius:m.role==="user"?"10px 10px 2px 10px":"10px 10px 10px 2px",
                    background:m.role==="user"?`linear-gradient(135deg,${rc},${rc}cc)`:T.panel,
                    border:`1px solid ${m.role==="user"?rc+"66":T.border}`,
                    fontSize:10,color:T.text,lineHeight:1.55,
                  }}>
                    <span dangerouslySetInnerHTML={{__html:m.msg}}/>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
                    <span style={{fontSize:7,color:T.muted,fontFamily:T.mono}}>{m.time}</span>
                    {m.role==="ai"&&ttsSupport&&(
                      <button onClick={()=>speakText(m.msg)} title="Speak in selected language"
                        disabled={speakLoading}
                        style={{fontSize:8,padding:"1px 5px",borderRadius:8,
                          border:`1px solid ${speakLoading?T.green+"44":T.border}`,
                          background:speakLoading?T.greenLo:"none",
                          cursor:speakLoading?"wait":"pointer",
                          color:speakLoading?T.green:T.muted,fontFamily:T.font,lineHeight:1.5}}>
                        {speakLoading?"⏳":"🔊"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {loading&&(
              <div style={{display:"flex",gap:7,alignItems:"flex-start"}}>
                <div style={{width:26,height:26,borderRadius:"50%",overflow:"hidden",flexShrink:0}}>
                  <img src={AVATAR_PVT} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}} alt="AI"/>
                </div>
                <div style={{padding:"8px 12px",borderRadius:"10px 10px 10px 2px",background:T.panel,border:`1px solid ${T.border}`,display:"flex",gap:4,alignItems:"center"}}>
                  {[0,1,2].map(j=><span key={j} style={{width:5,height:5,borderRadius:"50%",background:rc,animation:`aiPulse 1s ${j*0.25}s infinite`}}/>)}
                </div>
              </div>
            )}
            <div ref={chatEnd}/>
            {/* Recording overlay */}
            {listening&&(
              <div style={{position:"sticky",bottom:0,display:"flex",justifyContent:"center",paddingTop:4}}>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 14px",borderRadius:24,background:`${T.red}15`,border:`1px solid ${T.red}44`}}>
                  <div style={{display:"flex",alignItems:"flex-end",gap:2,height:16}}>
                    {[1,2,3,4,3,2,1].map((h,n)=>(
                      <div key={n} style={{width:2,background:T.red,borderRadius:1,height:2+h*2,animation:`waveBar ${0.5+n*0.08}s ease-in-out infinite`,animationDelay:`${n*0.08}s`}}/>
                    ))}
                  </div>
                  <span style={{fontSize:9,color:T.red,fontWeight:700}}>
                    🎙 {SPEECH_LANGS.find(l=>l.code===langCode)?.label||langCode}
                  </span>
                  <button onClick={stopListening}
                    style={{fontSize:8,padding:"2px 7px",borderRadius:10,border:`1px solid ${T.red}55`,background:`${T.red}20`,cursor:"pointer",color:T.red,fontFamily:T.font,fontWeight:700}}>
                    Stop
                  </button>
                </div>
              </div>
            )}
            {/* Speech error */}
            {speechErr&&(
              <div style={{borderRadius:8,overflow:"hidden",border:`1px solid ${T.red}35`,margin:"4px 0"}}>
                <div style={{padding:"7px 10px",background:`${T.red}12`,display:"flex",gap:7,alignItems:"flex-start"}}>
                  <span style={{fontSize:14,flexShrink:0}}>🔒</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:10,fontWeight:700,color:T.red,marginBottom:2}}>Mic Access Required</div>
                    <div style={{fontSize:9,color:T.text,lineHeight:1.6}}>{speechErr}</div>
                  </div>
                  <button onClick={()=>setSpeechErr("")} style={{background:"none",border:"none",cursor:"pointer",color:T.muted,fontSize:12,padding:0,flexShrink:0}}>✕</button>
                </div>
                <div style={{padding:"5px 10px",background:`${T.red}07`,borderTop:`1px solid ${T.red}20`}}>
                  <div style={{fontSize:9,color:T.muted,lineHeight:1.65}}>
                    Click 🔒 in address bar -> Site settings -> Microphone -> <b>Allow</b>
                  </div>
                  <button onClick={()=>{ setSpeechErr(""); requestMicAndListen(); }}
                    style={{marginTop:5,padding:"3px 10px",borderRadius:7,border:`1px solid ${T.blue}44`,
                      background:T.blueLo,cursor:"pointer",fontSize:9,fontWeight:700,color:T.blue,fontFamily:T.font}}>
                    🔄 Retry
                  </button>
                </div>
              </div>
            )}
            {/* Live transcript preview */}
            {transcript&&!listening&&(
              <div style={{fontSize:9,color:T.muted,padding:"3px 8px",fontStyle:"italic",textAlign:"center"}}>"{transcript}"</div>
            )}
          </div>

          {/* -- Input section -- */}
          <div style={{borderTop:`1px solid ${T.border}`,background:T.deep,flexShrink:0}}>
            {/* Language row */}
            <div style={{padding:"5px 10px",display:"flex",alignItems:"center",gap:4,borderBottom:`1px solid ${T.border}33`,flexWrap:"wrap"}}>
              <span style={{fontSize:8,color:T.muted,fontWeight:700,flexShrink:0}}>🌐</span>
              {TOP_LANGS.map(l=>(
                <button key={l.code} onClick={()=>setLangCode(l.code)}
                  title={l.label}
                  style={{padding:"2px 6px",borderRadius:10,border:`1px solid ${langCode===l.code?rc+"55":T.border}`,
                    background:langCode===l.code?`${rc}18`:"transparent",cursor:"pointer",
                    fontFamily:T.font,fontSize:9,color:langCode===l.code?rc:T.muted,
                    fontWeight:langCode===l.code?700:400,transition:"all .1s"}}>
                  {l.short}
                </button>
              ))}
              <button onClick={()=>setShowLang(p=>!p)}
                style={{fontSize:9,padding:"2px 6px",borderRadius:10,border:`1px solid ${T.border}`,background:"transparent",cursor:"pointer",color:T.muted,fontFamily:T.font}}>
                {showLang?"^":"v more"}
              </button>
              <button onClick={()=>{ setTtsEnabled(p=>!p); if(ttsEnabled) stopSpeaking(); }}
                style={{marginLeft:"auto",fontSize:9,padding:"2px 7px",borderRadius:10,
                  border:`1px solid ${ttsEnabled?T.green+"44":T.border}`,
                  background:ttsEnabled?T.greenLo:"transparent",cursor:"pointer",
                  color:ttsEnabled?T.green:T.muted,fontFamily:T.font,fontWeight:700}}>
                🔊 {ttsEnabled?"ON":"OFF"}
              </button>
            </div>
            {/* Extended lang picker */}
            {showLang&&(
              <div style={{padding:"6px 10px",display:"flex",flexWrap:"wrap",gap:4,background:T.faint,borderBottom:`1px solid ${T.border}33`}}>
                {SPEECH_LANGS.map(l=>(
                  <button key={l.code} onClick={()=>{setLangCode(l.code);setShowLang(false);}}
                    title={l.label}
                    style={{padding:"2px 7px",borderRadius:10,border:`1px solid ${langCode===l.code?rc+"55":T.border}`,
                      background:langCode===l.code?`${rc}18`:"transparent",cursor:"pointer",
                      fontFamily:T.font,fontSize:9,color:langCode===l.code?rc:T.muted,fontWeight:langCode===l.code?700:400}}>
                    {l.flag} {l.short}
                  </button>
                ))}
              </div>
            )}
            {/* Input row */}
            <div style={{padding:"8px 10px",display:"flex",gap:7,alignItems:"center"}}>
              {/* Mic button */}
              {speechSupport
                ? <button onClick={listening?stopListening:startListening}
                    style={{width:34,height:34,borderRadius:"50%",flexShrink:0,cursor:"pointer",outline:"none",
                      border:`2px solid ${listening?T.red+"80":rc+"60"}`,
                      background:listening?`${T.red}18`:`${rc}12`,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      boxShadow:listening?`0 0 0 ${micPulse?6:3}px ${T.red}25`:"none",
                      animation:listening?"micPulse 1.2s ease-in-out infinite":"none",
                      transition:"all .2s"}}>
                    <span style={{fontSize:14}}>{listening?"⏹":"🎙"}</span>
                  </button>
                : <div style={{width:34,height:34,borderRadius:"50%",flexShrink:0,border:`2px solid ${T.border}`,background:T.deep,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,opacity:.35}}>🎙</div>
              }
              {/* Text input */}
              <input
                value={input}
                onChange={e=>{setInput(e.target.value);setTranscript("");}}
                onKeyDown={e=>e.key==="Enter"&&sendMsg()}
                placeholder={listening
                  ? `🎙 Listening in ${SPEECH_LANGS.find(l=>l.code===langCode)?.short||"EN"}...`
                  : `Ask in ${SPEECH_LANGS.find(l=>l.code===langCode)?.short||"EN"}...`}
                style={{flex:1,background:listening?`${T.blue}08`:T.panel,border:`1.5px solid ${listening?rc+"55":T.border}`,
                  borderRadius:18,padding:"7px 12px",color:T.text,fontSize:10,fontFamily:T.font,outline:"none",transition:"all .2s"}}
              />
              {/* Send button */}
              <button onClick={()=>sendMsg()} disabled={!input.trim()||loading}
                style={{width:34,height:34,borderRadius:"50%",flexShrink:0,
                  background:input.trim()&&!loading?`linear-gradient(135deg,${rc},${rc}cc)`:T.deep,
                  border:`2px solid ${input.trim()&&!loading?rc:T.border}`,cursor:input.trim()&&!loading?"pointer":"default",
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,
                  transition:"all .18s",boxShadow:input.trim()&&!loading?`0 3px 10px ${rc}40`:"none"}}>
                {loading?"⏳":"➤"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -- Floating toggle button -- */}
      <div style={{position:"relative",pointerEvents:"all"}}>
        {!open&&pulse&&(
          <div style={{
            position:"absolute",bottom:"calc(100% + 8px)",right:0,
            background:T.card,border:`1px solid ${rc}44`,borderRadius:10,padding:"8px 12px",
            whiteSpace:"nowrap",boxShadow:"0 4px 16px rgba(26,34,53,.14)",minWidth:180,
            animation:"slideIn .3s ease",
          }}>
            <div style={{fontSize:10,fontWeight:700,color:rc}}>👋 Need help?</div>
            <div style={{fontSize:9,color:T.muted,marginTop:2}}>Ask the AI Copilot * Voice enabled 🎙</div>
            <div style={{position:"absolute",bottom:-5,right:18,width:10,height:10,background:T.card,border:`1px solid ${rc}44`,borderRadius:2,transform:"rotate(45deg)",borderTop:"none",borderLeft:"none"}}/>
          </div>
        )}
        <button onClick={()=>{setOpen(p=>!p);setPulse(false);}} style={{
          width:52,height:52,borderRadius:"50%",
          background:open?`${rc}cc`:`linear-gradient(135deg,#1E3A7A,${rc})`,
          border:`2px solid ${rc}`,
          boxShadow:`0 4px 20px ${rc}44, 0 0 0 ${pulse?"6px":"0px"} ${rc}18`,
          cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
          fontSize:22,transition:"all .2s",
          animation:pulse?"aiPulse 2s infinite":undefined,
        }}>
          {open?"✕":<img src={AVATAR_PVT} style={{width:38,height:38,borderRadius:"50%",objectFit:"cover",objectPosition:"top"}} alt="AI"/>}
        </button>
        {!open&&(
          <div style={{position:"absolute",top:-2,right:-2,width:14,height:14,borderRadius:"50%",
            background:T.green,border:`2px solid ${T.bg}`,display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:7,color:"#000",fontWeight:800}}>●</div>
        )}
      </div>
    </div>
  );
}

export const PageAccessControl = ({pageAccess, setPageAccess, currentRole, setCurrentRole, toast, logAudit}) => {
  const ALL_ROLES = [
    {id:"government", label:"Government Client", icon:"🏛", color:"#3B82F6", desc:"Municipal, Police, Defence, PSU clients"},
    {id:"private",    label:"Private Client",    icon:"🏢", color:"#7C3AED", desc:"Corporate, Commercial, Residential clients"},
  ];

  const togglePage = (role, pageId) => {
    setPageAccess(prev => {
      const current = new Set(prev[role]);
      if(current.has(pageId)) {
        if(pageId==="dashboard") { toast("Dashboard cannot be removed -- required page","warn"); return prev; }
        current.delete(pageId);
      } else {
        current.add(pageId);
      }
      const updated = {...prev, [role]:[...current]};
      logAudit&&logAudit("PAGE_ACCESS_UPDATED",`${role} -> ${pageId}`,current.has(pageId)?"Info":"Warn");
      toast(`Page access updated for ${role}`, "success");
      return updated;
    });
  };

  const selectAll  = (role) => {
    const all = ALL_PAGES.filter(p=>p.id!=="accesscontrol").map(p=>p.id);
    setPageAccess(prev=>({...prev,[role]:all}));
    toast(`All pages enabled for ${role}`);
  };
  const selectNone = (role) => {
    setPageAccess(prev=>({...prev,[role]:["dashboard"]}));
    toast(`All pages removed for ${role} (except Dashboard)`, "warn");
  };
  const resetDefault = (role) => {
    setPageAccess(prev=>({...prev,[role]:[...DEFAULT_PAGE_ACCESS[role]]}));
    toast(`${role} access reset to defaults`);
  };

  const sectionGroups = [...new Set(ALL_PAGES.filter(p=>p.id!=="accesscontrol").map(p=>p.section))];

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <PageHeader title="🔐 Page Access Control" sub="Super Admin configures which pages each client type can access - Changes apply instantly to sidebar navigation">
        <div style={{display:"flex",alignItems:"center",gap:8,background:`${T.accent}18`,border:`1px solid ${T.accent}44`,borderRadius:8,padding:"6px 12px"}}>
          <span style={{fontSize:11,color:T.accent,fontWeight:700}}>👑 Super Admin</span>
          <span style={{fontSize:9,color:T.muted}}>Always sees all pages</span>
        </div>
      </PageHeader>

      {/* Preview switcher */}
      <div style={{background:T.card,borderRadius:12,padding:14,border:`1px solid ${T.border}`}}>
        <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:10}}>Preview Navigation As</div>
        <div style={{display:"flex",gap:8}}>
          {[
            {id:"superadmin",label:"Super Admin",  icon:"SA",  c:T.accent},
            {id:"government",label:"Government",   icon:"🏛",  c:"#3B82F6"},
            {id:"private",   label:"Private",      icon:"🏢",  c:"#7C3AED"},
          ].map(r=>(
            <button key={r.id} onClick={()=>setCurrentRole(r.id)} style={{
              flex:1,padding:"10px 12px",borderRadius:8,cursor:"pointer",fontFamily:T.font,
              border:currentRole===r.id?`1.5px solid ${r.c}`:  `1px solid ${T.border}`,
              background:currentRole===r.id?`${r.c}18`:"transparent",transition:"all .15s",
            }}>
              <div style={{fontSize:14,marginBottom:3}}>{r.icon}</div>
              <div style={{fontSize:10,fontWeight:700,color:currentRole===r.id?r.c:T.muted}}>{r.label}</div>
              {currentRole===r.id&&<div style={{fontSize:8,color:r.c,marginTop:2}}>● Active</div>}
            </button>
          ))}
        </div>
        {currentRole!=="superadmin"&&(
          <div style={{marginTop:10,background:`${T.blue}10`,border:`1px solid ${T.blue}22`,borderRadius:6,padding:"6px 10px",fontSize:9,color:T.blue}}>
            i Sidebar now shows {currentRole} view with {(pageAccess[currentRole]||[]).length} page(s) visible. Switch back to Super Admin to see all pages.
          </div>
        )}
      </div>

      {/* Access matrix -- one card per role */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
        {ALL_ROLES.map(role=>{
          const allowed = new Set(pageAccess[role.id]||[]);
          const total   = ALL_PAGES.filter(p=>p.id!=="accesscontrol").length;
          const count   = allowed.size;
          const pct     = Math.round(count/total*100);
          return(
            <div key={role.id} style={{background:T.card,borderRadius:14,border:`1px solid ${role.color}33`,overflow:"hidden"}}>
              {/* Card header */}
              <div style={{background:`linear-gradient(135deg,${role.color}18,${role.color}06)`,padding:"14px 16px",borderBottom:`1px solid ${role.color}22`}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <div style={{width:36,height:36,borderRadius:9,background:`${role.color}22`,border:`1px solid ${role.color}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>{role.icon}</div>
                  <div>
                    <div style={{fontSize:13,fontWeight:800,color:role.color}}>{role.label}</div>
                    <div style={{fontSize:9,color:T.muted}}>{role.desc}</div>
                  </div>
                </div>
                {/* Progress bar */}
                <div style={{marginBottom:6}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:9,color:T.muted}}>Page Access</span>
                    <span style={{fontSize:9,fontWeight:700,color:role.color,fontFamily:T.mono}}>{count}/{total} pages ({pct}%)</span>
                  </div>
                  <div style={{height:5,background:T.deep,borderRadius:3}}>
                    <div style={{height:"100%",width:`${pct}%`,background:role.color,borderRadius:3,transition:"width .3s"}}/>
                  </div>
                </div>
                {/* Quick actions */}
                <div style={{display:"flex",gap:5}}>
                  <Btn sm v="ghost" onClick={()=>selectAll(role.id)}>✅ All</Btn>
                  <Btn sm v="ghost" onClick={()=>selectNone(role.id)}>🚫 None</Btn>
                  <Btn sm v="ghost" onClick={()=>resetDefault(role.id)}>Refresh Reset</Btn>
                </div>
              </div>

              {/* Page toggles grouped by section */}
              <div style={{padding:"10px 12px",maxHeight:400,overflowY:"auto"}}>
                {sectionGroups.map(section=>{
                  const pagesInSection = ALL_PAGES.filter(p=>p.section===section&&p.id!=="accesscontrol");
                  if(pagesInSection.length===0) return null;
                  return(
                    <div key={section} style={{marginBottom:10}}>
                      <div style={{fontSize:8,color:T.muted,fontWeight:800,letterSpacing:1,marginBottom:6,textTransform:"uppercase",paddingLeft:2}}>{section}</div>
                      <div style={{display:"flex",flexDirection:"column",gap:3}}>
                        {pagesInSection.map(page=>{
                          const isOn = allowed.has(page.id);
                          const isReq = page.id==="dashboard";
                          return(
                            <div
                              key={page.id}
                              onClick={()=>!isReq&&togglePage(role.id,page.id)}
                              style={{
                                display:"flex",alignItems:"center",gap:8,
                                padding:"6px 8px",borderRadius:7,cursor:isReq?"not-allowed":"pointer",
                                background:isOn?`${role.color}12`:"transparent",
                                border:`1px solid ${isOn?role.color+"33":T.border}`,
                                transition:"all .15s",
                                opacity:isReq?.8:1,
                              }}>
                              <span style={{fontSize:12,flexShrink:0}}>{page.icon}</span>
                              <span style={{fontSize:10,fontWeight:isOn?700:400,color:isOn?T.text:T.muted,flex:1}}>{page.label}</span>
                              {isReq&&<span style={{fontSize:8,color:T.muted,fontFamily:T.mono}}>required</span>}
                              {/* Toggle switch */}
                              <div style={{
                                width:28,height:15,borderRadius:8,
                                background:isOn?role.color:T.deep,
                                border:`1px solid ${isOn?role.color:T.border}`,
                                position:"relative",transition:"all .2s",flexShrink:0,
                              }}>
                                <div style={{
                                  position:"absolute",top:2,
                                  left:isOn?14:2,
                                  width:9,height:9,borderRadius:"50%",
                                  background:isOn?"#fff":T.muted,
                                  transition:"left .2s",
                                }}/>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary table */}
      <Panel title="ACCESS MATRIX SUMMARY">
        <div style={{display:"grid",gridTemplateColumns:"2fr repeat(3,1fr)",gap:0}}>
          {/* Header */}
          {["Page","Super Admin","Government","Private"].map((h,i)=>(
            <div key={h} style={{padding:"8px 12px",background:T.deep,fontSize:9,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:.8,borderBottom:`1px solid ${T.border}`,borderRight:i<3?`1px solid ${T.border}`:undefined}}>{h}</div>
          ))}
          {/* Rows */}
          {ALL_PAGES.filter(p=>p.id!=="accesscontrol").map((page,i)=>{
            const govHas  = (pageAccess.government||[]).includes(page.id);
            const privHas = (pageAccess.private||[]).includes(page.id);
            const bg = i%2===0?T.card:T.panel;
            return(
              <React.Fragment key={page.id}>
                <div style={{padding:"7px 12px",background:bg,fontSize:10,color:T.text,display:"flex",alignItems:"center",gap:6,borderRight:`1px solid ${T.border}`,borderBottom:`1px solid ${T.border}22`}}>
                  <span>{page.icon}</span><span>{page.label}</span>
                </div>
                <div style={{padding:"7px 12px",background:bg,textAlign:"center",borderRight:`1px solid ${T.border}`,borderBottom:`1px solid ${T.border}22`}}><span style={{color:T.green,fontSize:13}}>✓</span></div>
                <div style={{padding:"7px 12px",background:bg,textAlign:"center",borderRight:`1px solid ${T.border}`,borderBottom:`1px solid ${T.border}22`}}>
                  <span style={{color:govHas?T.green:T.red,fontSize:13}}>{govHas?"✓":"✗"}</span>
                </div>
                <div style={{padding:"7px 12px",background:bg,textAlign:"center",borderBottom:`1px solid ${T.border}22`}}>
                  <span style={{color:privHas?T.green:T.red,fontSize:13}}>{privHas?"✓":"✗"}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </Panel>
    </div>
  );
};
